import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { ALLOWED_EMAIL_DOMAINS } from '../config';

interface UserFormData {
  first_name: string;
  last_name: string;
  user_name: string;
  email: string;
  password: string;
  role_id: string;
  department_id: string;
  is_accounts_admin: boolean;
}

interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialData: any;
  roles: any[];
  departments: any[];
  onClose: () => void;
  onSave: (data: Record<string, any>) => Promise<void>;
}

const EMPTY: UserFormData = {
  first_name: '', last_name: '', user_name: '',
  email: '', password: '', role_id: '', department_id: '',
  is_accounts_admin: false,
};

export const UserFormModal: React.FC<Props> = ({
  isOpen, mode, initialData, roles, departments, onClose, onSave,
}) => {
  const [form, setForm] = useState<UserFormData>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    if (mode === 'edit' && initialData) {
      setForm({
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        user_name: initialData.user_name || '',
        email: initialData.email || '',
        password: '',
        role_id: roles.find(r => r.name === initialData.role)?.id?.toString() || '',
        department_id: departments.find(d => d.name === initialData.department)?.id?.toString() || '',
        is_accounts_admin: !!initialData.is_accounts_admin,
      });
    } else {
      setForm(EMPTY);
    }
  }, [isOpen, mode, initialData, roles, departments]);

  const set = (field: keyof UserFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    setError(null);

    // 1. Basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // 2. Allowed domain check
    const domain = form.email.split('@').pop()?.toLowerCase() || '';
    if (!ALLOWED_EMAIL_DOMAINS.includes(domain)) {
      setError(`Only official company email addresses (${ALLOWED_EMAIL_DOMAINS.join(', ')}) are allowed.`);
      return;
    }

    setSaving(true);
    try {
      const payload: Record<string, any> = {
        first_name: form.first_name,
        last_name: form.last_name,
        user_name: form.user_name,
        email: form.email,
        role_id: Number(form.role_id),
        department_id: Number(form.department_id),
        is_accounts_admin: form.is_accounts_admin,
      };
      if (form.password) payload.password = form.password;
      await onSave(payload);
    } catch (err: any) {
      console.error('Failed to save user:', err);
      setError(err.message || 'Failed to save user.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={mode === 'create' ? 'Create User' : 'Edit User'}>
      <div className="modal-body">
        {error && (
          <div
            style={{
              color: '#dc2626',
              backgroundColor: '#fee2e2',
              padding: '0.75rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              fontSize: '0.9rem',
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}
        <div className="form-row">
          <div className="form-field">
            <label>First Name</label>
            <input value={form.first_name} onChange={set('first_name')} />
          </div>
          <div className="form-field">
            <label>Last Name</label>
            <input value={form.last_name} onChange={set('last_name')} />
          </div>
        </div>
        <div className="form-field">
          <label>Username</label>
          <input value={form.user_name} onChange={set('user_name')} />
        </div>
        <div className="form-field">
          <label>Email</label>
          <input type="email" value={form.email} onChange={set('email')} />
        </div>
        <div className="form-field">
          <label>{mode === 'edit' ? 'Password (leave blank to keep)' : 'Password'}</label>
          <input type="password" value={form.password} onChange={set('password')} />
        </div>
        <div className="form-row">
          <div className="form-field">
            <label>Role</label>
            <select value={form.role_id} onChange={set('role_id')}>
              <option value="">Select role...</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Department</label>
            <select value={form.department_id} onChange={set('department_id')}>
              <option value="">Select department...</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        </div>
        
        <div className="form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
          <input 
            type="checkbox" 
            id="is_accounts_admin_checkbox"
            checked={form.is_accounts_admin} 
            onChange={e => setForm(f => ({ ...f, is_accounts_admin: e.target.checked }))} 
            style={{ width: 'auto', cursor: 'pointer', margin: 0 }}
          />
          <label htmlFor="is_accounts_admin_checkbox" style={{ cursor: 'pointer', marginBottom: 0, userSelect: 'none' }}>
            Designated Accounts Department Admin (Receives invoice upload notifications)
          </label>
        </div>
      </div>
      <div className="modal-footer">
        <button className="secondary-btn" onClick={onClose}>Cancel</button>
        <button className="primary-btn" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </Modal>
  );
};
